import api from "./api";

const getContributors = (search) => {
  return api
    .get("/state_set/data", {
      params: {
        stateSetId: "Contributor",
        ...search,
      },
    })
    .then((response) => {
      return response.data;
    });
};

const getContributor = (id) => {
  return api
    .get(`/state_set/data/Contributor/${id}`)
    .then((response) => {
      return response.data;
    });
};

const getContributorUi = () => {
  return api
    .get("/state_set/ui/Contributor")
    .then((response) => {
      return response.data;
    });
};

const createContributor = (contributor) => {
  return api
    .post("/state_set/data", {
      stateSetId: "Contributor",
      ...contributor,
    })
    .then((response) => {
      // Clear cache when contributor is created as it may affect membership counts
      clearMembershipCache();
      return response.data;
    });
};

const updateContributor = (contributor) => {
  return api
    .patch("/state_set/data", {
      stateSetId: "Contributor",
      ...contributor,
    })
    .then((response) => {
      // Clear cache when contributor is updated as it may affect membership counts
      clearMembershipCache();
      return response.data;
    });
};

const deleteContributor = (id) => {
  return api
    .delete(`/state_set/data/Contributor/${id}`)
    .then((response) => {
      // Clear cache when contributor is deleted as it may affect membership counts
      clearMembershipCache();
      return response.data;
    });
};

// Cache for groups and roles data with 2-minute expiry
let groupsCache = null;
let groupsCacheTime = 0;
let groupRolesCache = null;
let groupRolesCacheTime = 0;
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

const fetchGroupsData = async () => {
  const now = Date.now();

  // Fetch groups if cache is expired or empty
  if (!groupsCache || now - groupsCacheTime > CACHE_DURATION) {
    const groupsResponse = await api.get("/state_set/data", {
      params: {
        stateSetId: "Group",
        page: 0,
        pageSize: 99999,
      },
    });
    groupsCache = groupsResponse.data.list || [];
    groupsCacheTime = now;
  }

  // Fetch group roles if cache is expired or empty
  if (!groupRolesCache || now - groupRolesCacheTime > CACHE_DURATION) {
    const rolesResponse = await api.get("/state_set/data", {
      params: {
        stateSetId: "GroupRoles",
        page: 0,
        pageSize: 99999,
      },
    });
    groupRolesCache = rolesResponse.data.list || [];
    groupRolesCacheTime = now;
  }

  return { groups: groupsCache, groupRoles: groupRolesCache };
};

const getContributorGroupMemberships = async (contributorId) => {
  try {
    const { groups, groupRoles } = await fetchGroupsData();

    // Create a map of role ID to role details for quick lookup
    // GroupRoles uses 'id' field, not '_id'
    const roleMap = {};
    groupRoles.forEach((role) => {
      roleMap[role.id] = {
        name: role.name,
        description: role.description,
      };
    });

    const memberships = [];

    groups.forEach((group) => {
      if (group.group_role && Array.isArray(group.group_role)) {
        group.group_role.forEach((roleEntry) => {
          if (roleEntry.contributor === contributorId) {
            // roleEntry._id contains the GroupRoles id reference
            const roleId = roleEntry._id;
            const roleDetails = roleMap[roleId] || { name: "Unknown Role", description: "" };

            memberships.push({
              groupId: group._id,
              groupName: group.group_name,
              groupObjective: group.objective,
              roleId: roleId,
              roleName: roleDetails.name,
              roleDescription: roleDetails.description,
            });
          }
        });
      }
    });

    return memberships;
  } catch (error) {
    throw error;
  }
};

// Cache for teams and roles data with 2-minute expiry
let teamsCache = null;
let teamsCacheTime = 0;
let teamRolesCache = null;
let teamRolesCacheTime = 0;

const fetchTeamsData = async () => {
  const now = Date.now();

  // Fetch teams if cache is expired or empty
  if (!teamsCache || now - teamsCacheTime > CACHE_DURATION) {
    const teamsResponse = await api.get("/state_set/data", {
      params: {
        stateSetId: "Team",
        page: 0,
        pageSize: 99999,
      },
    });
    teamsCache = teamsResponse.data.list || [];
    teamsCacheTime = now;
  }

  // Fetch team roles if cache is expired or empty
  if (!teamRolesCache || now - teamRolesCacheTime > CACHE_DURATION) {
    const rolesResponse = await api.get("/state_set/data", {
      params: {
        stateSetId: "TeamRoles",
        page: 0,
        pageSize: 99999,
      },
    });
    teamRolesCache = rolesResponse.data.list || [];
    teamRolesCacheTime = now;
  }

  return { teams: teamsCache, teamRoles: teamRolesCache };
};

const getContributorTeamMemberships = async (contributorId) => {
  try {
    const { teams, teamRoles } = await fetchTeamsData();

    // Create a map of role ID to role details for quick lookup
    // TeamRoles uses 'id' field, not '_id'
    const roleMap = {};
    teamRoles.forEach((role) => {
      roleMap[role.id] = {
        name: role.name,
        description: role.description,
      };
    });

    const memberships = [];

    teams.forEach((team) => {
      if (team.team_role && Array.isArray(team.team_role)) {
        team.team_role.forEach((roleEntry) => {
          if (roleEntry.contributor === contributorId) {
            // roleEntry._id contains the TeamRoles id reference
            const roleId = roleEntry._id;
            const roleDetails = roleMap[roleId] || { name: "Unknown Role", description: "" };

            memberships.push({
              teamId: team._id,
              teamName: team.team_name,
              teamObjective: team.objective,
              roleId: roleId,
              roleName: roleDetails.name,
              roleDescription: roleDetails.description,
            });
          }
        });
      }
    });

    return memberships;
  } catch (error) {
    throw error;
  }
};

// Batch calculate membership counts for all contributors
const getAllMembershipCounts = async () => {
  try {
    const { groups, groupRoles } = await fetchGroupsData();
    const { teams, teamRoles } = await fetchTeamsData();

    const counts = {};

    // Calculate group membership counts
    groups.forEach((group) => {
      if (group.group_role && Array.isArray(group.group_role)) {
        group.group_role.forEach((roleEntry) => {
          const contributorId = roleEntry.contributor;
          if (!counts[contributorId]) {
            counts[contributorId] = { groups: 0, teams: 0 };
          }
          counts[contributorId].groups += 1;
        });
      }
    });

    // Calculate team membership counts
    teams.forEach((team) => {
      if (team.team_role && Array.isArray(team.team_role)) {
        team.team_role.forEach((roleEntry) => {
          const contributorId = roleEntry.contributor;
          if (!counts[contributorId]) {
            counts[contributorId] = { groups: 0, teams: 0 };
          }
          counts[contributorId].teams += 1;
        });
      }
    });

    return counts;
  } catch (error) {
    return {};
  }
};

// Clear all caches (useful when data is updated)
const clearMembershipCache = () => {
  groupsCache = null;
  groupsCacheTime = 0;
  groupRolesCache = null;
  groupRolesCacheTime = 0;
  teamsCache = null;
  teamsCacheTime = 0;
  teamRolesCache = null;
  teamRolesCacheTime = 0;
};

const ContributorService = {
  getContributors,
  getContributor,
  getContributorUi,
  createContributor,
  updateContributor,
  deleteContributor,
  getContributorGroupMemberships,
  getContributorTeamMemberships,
  getAllMembershipCounts,
  clearMembershipCache,
};

export default ContributorService;
